import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ChevronDown, ChevronRight, CalendarDays, Truck, CheckCircle2, Hourglass, FileText, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { CobroDetalle } from "./CobroDetalle";
import { Q, Pill, fechaLarga } from "./util";

const hora = (iso) => (iso ? new Date(iso).toLocaleTimeString("es-GT", { timeZone: "America/Guatemala", hour: "2-digit", minute: "2-digit" }) : "—");

const Accion = ({ r, onChanged, ver, setVer }) => {
  const conf = r.pagos.every((p) => p.estado_cuadre === "cuadrado");
  const facturado = r.md?.estado === "facturado";
  const algoFact = (r.md?.facturados || []).length > 0;
  const quien = r.pagos.find((p) => p.cuadre_por);
  const set = async (estado, lista, msg) => {
    try {
      for (const p of lista) await api.post("/domicilios/cuadre/pago", { order_id: r.id, pago_id: p.id, estado_cuadre: estado });
      toast.success(msg); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const desconfirmar = () => window.confirm(`¿Deshacer la confirmación de ${r.numero}? Volverá a REPORTADO y no sumará al saldo.`)
    && set("pendiente", r.pagos.filter((p) => !(r.md?.facturados || []).includes(p.id)), "Confirmación deshecha");
  return (
    <div className="flex flex-col items-start gap-1">
      <Pill tone={r.estado_entrega === "entregado" ? "green" : "zinc"} testid={`entrega-${r.numero}`}>{r.estado_entrega === "entregado" && <CheckCircle2 className="w-3 h-3" />}<span className="capitalize">{r.estado_entrega || "—"}</span></Pill>
      {conf ? <Pill tone="green"><CheckCircle2 className="w-3 h-3" />Confirmado</Pill>
        : <button onClick={() => set("cuadrado", r.pagos.filter((x) => x.estado_cuadre !== "cuadrado"), `Pago de ${r.numero} confirmado`)} data-testid={`confirmar-${r.numero}`}>
          <Pill tone="amber"><Hourglass className="w-3 h-3" />{r.nueva_regla ? "REPORTADO · Confirmar" : "Por verificar · Confirmar"}</Pill></button>}
      {conf && r.nueva_regla && !facturado && <button onClick={desconfirmar} className="text-[10px] text-zinc-600 underline" data-testid={`desconfirmar-${r.numero}`}><Undo2 className="inline w-3 h-3" /> Deshacer confirmación</button>}
      {conf && quien && <div className="text-[10px] text-zinc-500" data-testid={`confirmado-por-${r.numero}`}>{quien.cuadre_por} · {quien.cuadre_at ? new Date(quien.cuadre_at).toLocaleString("es-GT", { timeZone: "America/Guatemala" }) : ""}</div>}
      {r.nueva_regla
        ? <>
          {facturado ? <Pill tone="green" testid={`fact-estado-${r.numero}`}>FACTURADO</Pill> : algoFact ? <Pill tone="purple" testid={`fact-estado-${r.numero}`}>Facturado parcial</Pill> : null}
          <button onClick={() => setVer(!ver)} className="inline-flex items-center gap-1 rounded-lg border border-violet-600 text-violet-700 px-2 py-1 text-xs font-bold" data-testid={`ver-reporte-${r.numero}`}><FileText className="w-3.5 h-3.5" />Reporte del repartidor</button>
        </>
        : r.facturacion === "Facturado" ? <Pill tone="green">Facturado</Pill>
          : <Link to={`/facturacion?q=${r.numero}`} data-testid={`facturar-${r.numero}`}><Pill tone="purple"><FileText className="w-3 h-3" />Facturar{r.facturacion === "Parcial" ? " (parcial)" : ""}</Pill></Link>}
    </div>
  );
};

const Fila = ({ r, fecha, onChanged }) => {
  const [ver, setVer] = useState(true);
  return (
    <Fragment>
      <tr className="border-t align-top grid grid-cols-2 gap-x-2 md:table-row" data-testid={`ciudad-fila-${r.numero}`}>
        <td className="px-2 py-2 col-span-2 md:table-cell">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-bold text-xs uppercase" data-testid={`ciudad-cliente-${r.numero}`}>{r.cliente}</span>
            <Link to={`/pedidos/${r.id}`} className="whitespace-nowrap rounded bg-violet-700 hover:bg-violet-800 text-white text-[10px] font-bold px-1.5 py-0.5 underline-offset-2 hover:underline" data-testid={`ciudad-link-${r.numero}`}>{r.numero}</Link>
          </div>
        </td>
        <td className="px-2 py-1 md:py-2 whitespace-nowrap text-xs md:table-cell"><Truck className="inline w-4 h-4 text-emerald-600 mr-1" />{hora(r.hora_entrega)}</td>
        <td className="px-2 py-1 md:py-2 text-xs md:table-cell">{r.productos}</td>
        <td className="px-2 py-1 md:py-2 text-xs md:min-w-[170px] md:table-cell">
          <div data-testid={`ciudad-anticipo-${r.numero}`}>
            <div>Total: <b><Q v={r.total} /></b></div>
            <div>Anticipo: <b><Q v={r.anticipo.monto} /></b>{r.anticipo.monto > 0 && <span className="text-zinc-500"> ({r.anticipo.banco || r.anticipo.metodo})</span>}</div>
          </div>
          <div className="mt-1 rounded bg-violet-50 px-2 py-1 font-black" data-testid={`ciudad-debe-${r.numero}`}>Debe cobrar: <Q v={r.debe_cobrar} /></div>
          {r.pagos.map((p) => <div key={p.id} className="flex justify-between gap-2 mt-0.5"><span>{p.caja}</span><Q v={p.monto} /></div>)}
          {r.exceso > 0 && <div className="text-red-600 font-bold text-[11px]" data-testid={`ciudad-exceso-${r.numero}`}>Cobró de más: <Q v={r.exceso} /></div>}
        </td>
        <td className="px-2 py-1 md:py-2 md:table-cell"><Accion r={r} onChanged={onChanged} ver={ver} setVer={setVer} /></td>
      </tr>
      {r.nueva_regla && ver && <tr className="block md:table-row"><td colSpan={5} className="p-0 block md:table-cell"><div><CobroDetalle r={r} fecha={fecha} onChanged={onChanged} /></div></td></tr>}
    </Fragment>
  );
};

export const DiaReporte = ({ d, abierto, onChanged }) => {
  const [open, setOpen] = useState(abierto);
  const pend = d.n - d.confirmados;
  return (
    <div className="rounded-xl border bg-white overflow-hidden" data-testid={`dia-${d.fecha}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex flex-wrap items-center gap-2 px-3 py-2 text-left" data-testid={`dia-toggle-${d.fecha}`}>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <CalendarDays className="w-4 h-4 text-violet-700" /><b className="mr-auto">{fechaLarga(d.fecha)}</b>
        <Pill tone="zinc"><Truck className="w-3 h-3" />{d.n} Pedidos</Pill>
        {pend === 0 ? <Pill tone="green">Todo revisado</Pill> : <><Pill tone="green">{d.confirmados} Confirmados</Pill><Pill tone="amber">{pend} Por confirmar</Pill></>}
        <span className="text-sm">Total cobrado: <b><Q v={d.total} /></b></span>
      </button>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm block md:table">
            <thead className="bg-violet-50 text-xs text-left hidden md:table-header-group">
              <tr>{["Cliente / Pedido", "Entrega", "Productos (cant.)", "Anticipo / Forma(s) de pago", "Estado / Acción"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr>
            </thead>
            <tbody className="block md:table-row-group">{d.pedidos.map((r) => <Fila key={r.id} r={r} fecha={d.fecha} onChanged={onChanged} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
